import { isAfter, startOfDay } from 'date-fns';
import { addDays } from 'date-fns/addDays';
import { addMonths } from 'date-fns/addMonths';
import { addWeeks } from 'date-fns/addWeeks';
import { addYears } from 'date-fns/addYears';
import { subDays } from 'date-fns/subDays';
import { subMonths } from 'date-fns/subMonths';
import { subWeeks } from 'date-fns/subWeeks';
import { subYears } from 'date-fns/subYears';
import { formatShortDate } from 'src/utils/dateFormatter';

import {
  Invoice,
  StripePlan,
  StripeSubscription,
  Subscription,
} from './_types';

export const CANCEL_BEHAVIORS = [
  { id: 'cancel', name: 'Cancel' },
  { id: 'auto_renew', name: 'Auto Renew' },
  { id: 'none', name: 'None' },
];

export const isSubscriptionPaidByOtherUser = (
  subscription: StripeSubscription
): boolean => {
  return (
    !!subscription?.paidById && subscription?.paidById !== subscription.userId
  );
};

export const canInvoiceBeVoided = (invoice: Invoice): boolean => {
  return ['draft', 'open'].includes(invoice.status);
};

export const canInvoiceBeDeleted = (invoice: Invoice): boolean => {
  return invoice.status === 'draft' && !invoice.subscriptionId;
};

export const canInvoiceBeEdited = (invoice: Invoice): boolean => {
  return invoice.status === 'draft' && invoice.manual;
};

export const canInvoicePaymentMethodBeUpdated = (invoice: Invoice): boolean => {
  return ['open', 'draft'].includes(invoice.status);
};

export const isInvoiceClosed = (invoice: Invoice): boolean => {
  return 'open' !== invoice.status;
};

export function getSubscriptionLastPaymentDate(
  subscription: StripeSubscription
): Date {
  const nextPaymentAttempt = subscription.currentPeriodEnd || new Date();
  let numPayments = parseInt(String(subscription.totalPayments || 0), 10);
  const totalPayments = subscription.cancelAfter;
  if (subscription.startOn) numPayments += 1; // if a scheduled subscription, need to include that first payment
  let paymentsRemaining = (totalPayments || 0) - numPayments - 1; // subtract 1 for first payment since we're going off of current_period_end date

  const interval = subscription.plan?.interval;
  const intervalCount = subscription.plan?.intervalCount;
  let nextBillingEnd = nextPaymentAttempt;

  const addInterval = (date: Date, count: number) => {
    switch (interval) {
      case 'day':
        return addDays(date, count);
      case 'week':
        return addWeeks(date, count);
      case 'month':
        return addMonths(date, count);
      case 'year':
        return addYears(date, count);
      default:
        return date;
    }
  };

  const subtractInterval = (date: Date, count: number) => {
    switch (interval) {
      case 'day':
        return subDays(date, count);
      case 'week':
        return subWeeks(date, count);
      case 'month':
        return subMonths(date, count);
      case 'year':
        return subYears(date, count);
      default:
        return date;
    }
  };

  // if numPayments is greater than or equal to total totalPayments (e.g. they have already made their "last payment")
  for (; paymentsRemaining < 0; paymentsRemaining += 1) {
    nextBillingEnd = subtractInterval(nextBillingEnd, intervalCount);
  }

  // if numPayments is less than totalPayments (e.g. they have not made their "last payment" yet)
  for (; paymentsRemaining > 0; paymentsRemaining -= 1) {
    nextBillingEnd = addInterval(nextBillingEnd, intervalCount);
  }
  return nextBillingEnd;
}

export function getSubscriptionEndDate(
  subscription: StripeSubscription
): Date | undefined {
  const lastPayment = getSubscriptionLastPaymentDate(subscription);

  if (!lastPayment) return undefined;

  const interval = subscription.plan?.interval;
  const intervalCount = subscription.plan?.intervalCount;

  const addInterval = (date: Date, count: number) => {
    switch (interval) {
      case 'day':
        return addDays(date, count);
      case 'week':
        return addWeeks(date, count);
      case 'month':
        return addMonths(date, count);
      case 'year':
        return addYears(date, count);
      default:
        return date;
    }
  };

  return addInterval(lastPayment, intervalCount);
}

export const getScheduledSubscriptions = (subscriptions: Subscription[]) => {
  return subscriptions.filter((subscription) => {
    return (
      subscription.startOn != null &&
      isAfter(subscription.startOn, startOfDay(new Date()))
    );
  });
};

export const getPackageSubscriptions = (subscriptions: Subscription[]) => {
  return subscriptions.filter((subscription) => {
    return subscription.subscribeableType == 'Fbm::Package';
  });
};

export function getAnchorName(stripePlan: StripePlan): string {
  const anchor = stripePlan?.metadata?.billingCycleAnchor;

  if (anchor) {
    if (stripePlan.interval === 'year') {
      const [month, day] = anchor.split('-');
      const date = new Date(
        new Date().getFullYear(),
        parseInt(month) - 1,
        parseInt(day)
      );

      return formatShortDate(date);
    } else {
      if (typeof anchor === 'string' && !/\d/.test(anchor)) {
        return anchor;
      }

      const day = parseInt(anchor);

      if (!isNaN(day)) {
        const j = day % 10;
        const k = day % 100;
        const suffix =
          j === 1 && k !== 11
            ? 'st'
            : j === 2 && k !== 12
            ? 'nd'
            : j === 3 && k !== 13
            ? 'rd'
            : 'th';

        return `${day}${suffix}`;
      }
    }
  }

  return '';
}

export function getBehaviorName(stripePlan: StripePlan): string {
  const behavior = stripePlan?.metadata?.prorate;

  if (behavior === 'no_prorate') {
    return 'charge full amount';
  } else if (behavior === 'prorate') {
    return 'prorate first charge';
  } else if (behavior === 'no_charge') {
    return 'do not charge';
  }

  return '';
}

export function getCancelBehaviorName(stripePlan: StripePlan): string {
  const behavior = stripePlan?.metadata?.cancelAfterBehavior;

  if (behavior) {
    const b = CANCEL_BEHAVIORS.find((v) => v.id === behavior);
    if (b) return b.name;
  }

  return 'Cancel';
}
